import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Modal, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import logoHorizontal from '../../../assets/images/logo-horizontal.svg';
import apiService from "../../../utils/api";

const { Title } = Typography;

interface LoginFormData {
  username: string;
  password: string;
}

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [forgotModalVisible, setForgotModalVisible] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotForm] = Form.useForm();

  const from = (location.state as any)?.from?.pathname || '/home';
  const sessionExpiredMessage = (location.state as any)?.message;

  // Pre-warm the backend server on Render when they land on the login page
  React.useEffect(() => {
    const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:4000';
    fetch(`${baseUrl}/api/health`)
      .then(res => res.json())
      .then(data => console.log('Server pre-warmed:', data.message))
      .catch(err => console.log('Server waking up...'));
  }, []);

  const onFinish = async (values: LoginFormData) => {
    try {
      setLoading(true);
      setError(null);

      console.log('Attempting login with:', values.username);
      const success = await login(values.username, values.password);

      console.log('Login result:', success);
      console.log('Redirecting to:', from);

      if (success) {
        navigate(from, { replace: true });
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (values: { username: string }) => {
    try {
      setForgotLoading(true);
      const res = await apiService.forgotPassword(values.username);
      message.success(res.message || 'Password reset link sent to your email.');
      setForgotModalVisible(false);
      forgotForm.resetFields();
    } catch (err: any) {
      message.error(err.message || 'Failed to request password reset');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <Card style={{ width: 400, boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img
            src={logoHorizontal}
            alt="VetanaHR"
            style={{
              height: '60px',
              width: 'auto',
              marginBottom: 16,
              objectFit: 'contain'
            }}
          />
          <Title level={4} style={{ color: '#666', margin: 0 }}>
            Sign In
          </Title>
        </div>

        {sessionExpiredMessage && (
          <Alert
            message={sessionExpiredMessage}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Form
          name="login"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
        >
          <Form.Item
            name="username"
            label="Username"
            rules={[
              { required: true, message: 'Please input your username!' },
              { min: 3, message: 'Username must be at least 3 characters!' }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Enter your username"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please input your password!' },
              { min: 6, message: 'Password must be at least 6 characters!' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter your password"
              size="large"
            />
          </Form.Item>

          <div style={{ textAlign: 'right', marginBottom: 16 }}>
            <Button
              type="link"
              onClick={() => setForgotModalVisible(true)}
              style={{ padding: 0, height: 'auto' }}
            >
              Forgot Password?
            </Button>
          </div>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="large"
              block
              style={{ height: 48, fontSize: 16 }}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Typography.Text type="secondary">
            Don't have an account? Contact your administrator
          </Typography.Text>
        </div>
      </Card>

      <Modal
        title="Forgot Password"
        open={forgotModalVisible}
        onCancel={() => {
          setForgotModalVisible(false);
          forgotForm.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary">
          Enter your username below. If the account exists, we will send a password reset link to your registered email.
        </Typography.Paragraph>
        <Form
          form={forgotForm}
          name="forgotPasswordForm"
          onFinish={handleForgotPassword}
          layout="vertical"
        >
          <Form.Item
            name="username"
            label="Username"
            rules={[
              { required: true, message: 'Please input your username!' },
              { min: 3, message: 'Username must be at least 3 characters!' }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Enter your username"
              size="large"
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button onClick={() => setForgotModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={forgotLoading}>
                Submit
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Login; 